import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { API } from "@/constants";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  FileText, Plus, Clock, AlertTriangle, CheckCircle, XCircle, Calendar,
  Scale, Download, Search, Activity, BarChart3, Trash2, ArrowLeft,
  ChevronRight, Loader2, Send, Gavel, Flag, Bot
} from "lucide-react";
import LinkToCase from "@/components/shared/LinkToCase";

const StatusBadge = ({ status, date }) => {
  const today = new Date().toISOString().slice(0, 10);
  const isOverdue = status === "pending" && date < today;
  if (isOverdue) return <span className="px-2 py-0.5 rounded text-xs font-bold bg-red-100 text-red-700">OVERDUE</span>;
  if (status === "met") return <span className="px-2 py-0.5 rounded text-xs font-bold bg-green-100 text-green-700">MET</span>;
  if (status === "waived") return <span className="px-2 py-0.5 rounded text-xs font-bold bg-slate-100 text-slate-600">WAIVED</span>;
  return <span className="px-2 py-0.5 rounded text-xs font-bold bg-amber-100 text-amber-700">PENDING</span>;
};

const CatBadge = ({ cat }) => {
  const colors = {
    initiating: "bg-blue-100 text-blue-700", responsive: "bg-purple-100 text-purple-700",
    discovery: "bg-teal-100 text-teal-700", motions: "bg-orange-100 text-orange-700",
    court_orders: "bg-slate-100 text-slate-700", trial: "bg-red-100 text-red-700",
    post_trial: "bg-pink-100 text-pink-700", settlement: "bg-green-100 text-green-700",
  };
  return <span className={`px-2 py-0.5 rounded text-xs font-medium ${colors[cat] || "bg-gray-100"}`}>{(cat || "").replace("_", " ")}</span>;
};

export default function CourtFilingCyclesPage() {
  const [tab, setTab] = useState("dockets");
  const [dockets, setDockets] = useState([]);
  const [courts, setCourts] = useState({ federal_districts: [], federal_circuits: [], state_courts: [] });
  const [filingTypes, setFilingTypes] = useState({});
  const [analytics, setAnalytics] = useState(null);
  const [upcomingDL, setUpcomingDL] = useState({ overdue: [], upcoming: [] });
  const [selectedDocket, setSelectedDocket] = useState(null);
  const [loading, setLoading] = useState(false);

  // Docket creation form
  const [docketForm, setDocketForm] = useState({
    case_title: "", docket_number: "", court_id: "", court_type: "federal",
    judge: "", plaintiff: "", defendant: "", case_type: "",
  });

  // Filing creation form
  const [filingForm, setFilingForm] = useState({
    filing_type: "", filing_date: new Date().toISOString().slice(0, 10),
    filed_by: "", document_number: "", description: "", deadline_date: "", deadline_note: "",
  });

  // AI Parse
  const [parseText, setParseText] = useState("");
  const [parseResult, setParsResult] = useState(null);
  const [parsing, setParsing] = useState(false);

  const load = async () => {
    const [dk, ct, ft, an, dl] = await Promise.all([
      axios.get(`${API}/court-filings/dockets`).catch(() => ({ data: { dockets: [] } })),
      axios.get(`${API}/court-filings/courts`).catch(() => ({ data: {} })),
      axios.get(`${API}/court-filings/filing-types`).catch(() => ({ data: { categories: {} } })),
      axios.get(`${API}/court-filings/analytics`).catch(() => ({ data: null })),
      axios.get(`${API}/court-filings/deadlines/upcoming?days=60`).catch(() => ({ data: { overdue: [], upcoming: [] } })),
    ]);
    setDockets(dk.data.dockets || []);
    setCourts(ct.data);
    setFilingTypes(ft.data.categories || {});
    setAnalytics(an.data);
    setUpcomingDL(dl.data);
  };

  useEffect(() => { load(); }, []);

  const courtOptions = useMemo(() => {
    const all = [];
    (courts.federal_districts || []).forEach(c => all.push({ ...c, group: "Federal" }));
    (courts.state_courts || []).forEach(c => all.push({ ...c, group: "State" }));
    return all;
  }, [courts]);

  const flatFilingTypes = useMemo(() => {
    const all = [];
    Object.entries(filingTypes).forEach(([cat, items]) => {
      items.forEach(ft => all.push({ ...ft, category: cat }));
    });
    return all;
  }, [filingTypes]);

  // ─── DOCKET ACTIONS ─────────────────────────────────
  const createDocket = async () => {
    if (!docketForm.case_title || !docketForm.court_id) return;
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/court-filings/dockets`, docketForm);
      setSelectedDocket(data.data);
      setDocketForm({ case_title: "", docket_number: "", court_id: "", court_type: "federal", judge: "", plaintiff: "", defendant: "", case_type: "" });
      setTab("docket-detail");
      load();
    } catch (err) { alert(err.response?.data?.detail || "Failed"); }
    finally { setLoading(false); }
  };

  const openDocket = async (id) => {
    const { data } = await axios.get(`${API}/court-filings/dockets/${id}`);
    setSelectedDocket(data.data);
    setTab("docket-detail");
  };

  const deleteDocket = async (id) => {
    if (!window.confirm("Delete this docket?")) return;
    await axios.delete(`${API}/court-filings/dockets/${id}`);
    setSelectedDocket(null);
    setTab("dockets");
    load();
  };

  // ─── FILING ACTIONS ─────────────────────────────────
  const addFiling = async () => {
    if (!filingForm.filing_type || !filingForm.filing_date) return;
    setLoading(true);
    try {
      await axios.post(`${API}/court-filings/filings`, { ...filingForm, docket_id: selectedDocket.id });
      const { data } = await axios.get(`${API}/court-filings/dockets/${selectedDocket.id}`);
      setSelectedDocket(data.data);
      setFilingForm({ filing_type: "", filing_date: new Date().toISOString().slice(0, 10), filed_by: "", document_number: "", description: "", deadline_date: "", deadline_note: "" });
      load();
    } catch (err) { alert(err.response?.data?.detail || "Failed"); }
    finally { setLoading(false); }
  };

  const markDeadline = async (dlId, status) => {
    await axios.patch(`${API}/court-filings/deadlines/${selectedDocket.id}/${dlId}?status=${status}`);
    const { data } = await axios.get(`${API}/court-filings/dockets/${selectedDocket.id}`);
    setSelectedDocket(data.data);
    load();
  };

  // ─── AI PARSE ───────────────────────────────────────
  const parseDocket = async () => {
    if (!parseText.trim()) return;
    setParsing(true);
    setParsResult(null);
    try {
      const { data } = await axios.post(`${API}/court-filings/parse-docket`, { docket_text: parseText });
      setParsResult(data.data);
    } catch (err) { alert(err.response?.data?.detail || "Parse failed"); }
    finally { setParsing(false); }
  };

  const importParsed = async () => {
    if (!parseResult) return;
    setLoading(true);
    try {
      // Create docket from parsed data
      const dkt = await axios.post(`${API}/court-filings/dockets`, {
        case_title: parseResult.case_title || "Parsed Docket",
        docket_number: parseResult.docket_number || "",
        court_id: parseResult.court_id || "",
        court_type: parseResult.court_type || "federal",
        judge: parseResult.judge || "",
        plaintiff: parseResult.plaintiff || "",
        defendant: parseResult.defendant || "",
      });
      const dktId = dkt.data.data.id;
      // Add all filings
      for (const f of (parseResult.filings || [])) {
        await axios.post(`${API}/court-filings/filings`, {
          docket_id: dktId,
          filing_type: f.filing_type || "other",
          filing_date: f.filing_date || new Date().toISOString().slice(0, 10),
          filed_by: f.filed_by || "",
          document_number: f.document_number || "",
          description: f.description || "",
        });
      }
      const { data } = await axios.get(`${API}/court-filings/dockets/${dktId}`);
      setSelectedDocket(data.data);
      setTab("docket-detail");
      setParsResult(null);
      setParseText("");
      load();
    } catch (err) { alert("Import failed"); }
    finally { setLoading(false); }
  };

  // ─── RENDER ─────────────────────────────────────────
  const sortedFilings = (selectedDocket?.filings || []).slice().sort((a, b) => (a.filing_date || "").localeCompare(b.filing_date || ""));
  const sortedDeadlines = (selectedDocket?.deadlines || []).slice().sort((a, b) => (a.deadline_date || "").localeCompare(b.deadline_date || ""));

  return (
    <div className="space-y-6" data-testid="court-filing-cycles-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Court Filing Cycles</h1>
        <p className="text-slate-500 mt-1">Federal & State docket management, deadline tracking, and AI-assisted parsing</p>
      </div>

      {/* Deadline Alert Banner */}
      {(upcomingDL.total_overdue > 0) && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3" data-testid="overdue-alert">
          <AlertTriangle className="w-5 h-5 text-red-600 shrink-0" />
          <div>
            <span className="font-bold text-red-800">{upcomingDL.total_overdue} Overdue Deadline{upcomingDL.total_overdue > 1 ? "s" : ""}</span>
            <span className="text-red-600 text-sm ml-2">Action required immediately</span>
          </div>
          <Button size="sm" variant="outline" className="ml-auto border-red-300 text-red-700" onClick={() => setTab("deadlines")} data-testid="view-overdue-btn">View</Button>
        </div>
      )}

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="dockets" data-testid="tab-dockets">Dockets ({dockets.length})</TabsTrigger>
          <TabsTrigger value="new-docket" data-testid="tab-new-docket"><Plus className="w-3 h-3 mr-1" />New</TabsTrigger>
          <TabsTrigger value="deadlines" data-testid="tab-deadlines">Deadlines</TabsTrigger>
          <TabsTrigger value="ai-parse" data-testid="tab-ai-parse"><Bot className="w-3 h-3 mr-1" />AI Parse</TabsTrigger>
          <TabsTrigger value="analytics" data-testid="tab-analytics">Analytics</TabsTrigger>
          {selectedDocket && <TabsTrigger value="docket-detail" data-testid="tab-docket-detail">📋 {selectedDocket.case_title?.slice(0, 20)}</TabsTrigger>}
        </TabsList>

        {/* ═══ DOCKETS LIST ═══ */}
        <TabsContent value="dockets" className="mt-4 space-y-3">
          {dockets.length === 0 ? (
            <Card><CardContent className="py-12 text-center text-slate-500">
              <Scale className="w-10 h-10 mx-auto mb-3 text-slate-300" />
              <p className="font-medium">No dockets tracked yet</p>
              <p className="text-sm mt-1">Create a new docket or use AI Parse to import one.</p>
              <Button className="mt-4" onClick={() => setTab("new-docket")} data-testid="first-docket-btn"><Plus className="w-4 h-4 mr-1" />Create Docket</Button>
            </CardContent></Card>
          ) : dockets.map(d => (
            <Card key={d.id} className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => openDocket(d.id)} data-testid={`docket-card-${d.id}`}>
              <CardContent className="py-4 flex items-center justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <Gavel className="w-4 h-4 text-blue-600 shrink-0" />
                    <span className="font-semibold truncate">{d.case_title}</span>
                    {d.docket_number && <span className="text-xs text-slate-400">#{d.docket_number}</span>}
                  </div>
                  <div className="flex items-center gap-3 mt-1 text-xs text-slate-500">
                    <span>{d.court_name}</span>
                    <span>{d.filing_count} filings</span>
                    {d.deadline_count > 0 && <span className="text-amber-600 font-medium">{d.deadline_count} pending deadlines</span>}
                  </div>
                </div>
                <ChevronRight className="w-4 h-4 text-slate-400 shrink-0" />
              </CardContent>
            </Card>
          ))}
        </TabsContent>

        {/* ═══ NEW DOCKET ═══ */}
        <TabsContent value="new-docket" className="mt-4">
          <Card>
            <CardHeader><CardTitle className="text-lg flex items-center gap-2"><Plus className="w-5 h-5 text-blue-600" />Create New Docket</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div><Label>Case Title *</Label><Input data-testid="new-docket-title" value={docketForm.case_title} onChange={e => setDocketForm(f => ({...f, case_title: e.target.value}))} placeholder="Smith v. Jones" /></div>
                <div><Label>Docket Number</Label><Input data-testid="new-docket-number" value={docketForm.docket_number} onChange={e => setDocketForm(f => ({...f, docket_number: e.target.value}))} placeholder="2:24-cv-01234" /></div>
                <div>
                  <Label>Court Type</Label>
                  <Select value={docketForm.court_type} onValueChange={v => setDocketForm(f => ({...f, court_type: v, court_id: ""}))}>
                    <SelectTrigger data-testid="court-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="federal">Federal</SelectItem>
                      <SelectItem value="state">State</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Court *</Label>
                  <Select value={docketForm.court_id} onValueChange={v => setDocketForm(f => ({...f, court_id: v}))}>
                    <SelectTrigger data-testid="court-select"><SelectValue placeholder="Select court..." /></SelectTrigger>
                    <SelectContent>
                      {courtOptions.filter(c => (docketForm.court_type === "federal" ? c.type === "federal" && c.parent === "district" : c.type === "state")).map(c => (
                        <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Judge</Label><Input data-testid="new-docket-judge" value={docketForm.judge} onChange={e => setDocketForm(f => ({...f, judge: e.target.value}))} placeholder="Hon. John Smith" /></div>
                <div><Label>Case Type</Label><Input data-testid="new-docket-case-type" value={docketForm.case_type} onChange={e => setDocketForm(f => ({...f, case_type: e.target.value}))} placeholder="Personal Injury, Contract..." /></div>
                <div><Label>Plaintiff</Label><Input data-testid="new-docket-plaintiff" value={docketForm.plaintiff} onChange={e => setDocketForm(f => ({...f, plaintiff: e.target.value}))} /></div>
                <div><Label>Defendant</Label><Input data-testid="new-docket-defendant" value={docketForm.defendant} onChange={e => setDocketForm(f => ({...f, defendant: e.target.value}))} /></div>
              </div>
              <Button data-testid="create-docket-btn" onClick={createDocket} disabled={loading || !docketForm.case_title || !docketForm.court_id} className="bg-blue-600 hover:bg-blue-700 text-white">
                {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}Create Docket
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ═══ DOCKET DETAIL ═══ */}
        <TabsContent value="docket-detail" className="mt-4">
          {selectedDocket ? (
            <div className="space-y-4">
              {/* Header */}
              <div className="flex items-center justify-between">
                <button onClick={() => { setSelectedDocket(null); setTab("dockets"); }} className="text-sm text-slate-500 hover:text-slate-700 flex items-center gap-1"><ArrowLeft className="w-4 h-4" />All Dockets</button>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => window.open(`${API}/court-filings/dockets/${selectedDocket.id}/report-pdf?report_type=full`, '_blank')} data-testid="export-docket-pdf"><Download className="w-3 h-3 mr-1" />Export PDF</Button>
                  <Button variant="outline" size="sm" className="text-red-600" onClick={() => deleteDocket(selectedDocket.id)}><Trash2 className="w-3 h-3 mr-1" />Delete</Button>
                </div>
              </div>

              <Card className="border-l-4 border-l-blue-600">
                <CardContent className="pt-5">
                  <h2 className="text-lg font-bold">{selectedDocket.case_title}</h2>
                  <div className="flex flex-wrap gap-3 mt-1 text-sm text-slate-500">
                    <span>{selectedDocket.court_name}</span>
                    {selectedDocket.docket_number && <span>#{selectedDocket.docket_number}</span>}
                    {selectedDocket.judge && <span>Judge: {selectedDocket.judge}</span>}
                  </div>
                  <div className="flex flex-wrap gap-3 mt-1 text-xs text-slate-400">
                    {selectedDocket.plaintiff && <span>Plaintiff: {selectedDocket.plaintiff}</span>}
                    {selectedDocket.defendant && <span>Defendant: {selectedDocket.defendant}</span>}
                  </div>
                </CardContent>
              </Card>

              <LinkToCase
                itemId={selectedDocket.id}
                itemType="court_docket"
                title={`Docket: ${selectedDocket.case_title}`}
                summary={`${selectedDocket.court_name} | ${sortedFilings.length} filings | ${sortedDeadlines.filter(d => d.status === "pending").length} pending deadlines`}
              />

              {/* Add Filing */}
              <Card>
                <CardHeader><CardTitle className="text-base flex items-center gap-2"><Plus className="w-4 h-4 text-blue-600" />Add Filing Entry</CardTitle></CardHeader>
                <CardContent className="space-y-3">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div>
                      <Label className="text-xs">Filing Type *</Label>
                      <Select value={filingForm.filing_type} onValueChange={v => setFilingForm(f => ({...f, filing_type: v}))}>
                        <SelectTrigger data-testid="filing-type-select" className="h-9"><SelectValue placeholder="Select filing type..." /></SelectTrigger>
                        <SelectContent>
                          {Object.entries(filingTypes).map(([cat, items]) => (
                            items.map(ft => <SelectItem key={ft.id} value={ft.id}>{ft.name} ({cat.replace("_"," ")})</SelectItem>)
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div><Label className="text-xs">Filing Date *</Label><Input className="h-9" type="date" data-testid="filing-date" value={filingForm.filing_date} onChange={e => setFilingForm(f => ({...f, filing_date: e.target.value}))} /></div>
                    <div>
                      <Label className="text-xs">Filed By</Label>
                      <Select value={filingForm.filed_by || "none"} onValueChange={v => setFilingForm(f => ({...f, filed_by: v === "none" ? "" : v}))}>
                        <SelectTrigger className="h-9" data-testid="filed-by-select"><SelectValue placeholder="Who filed?" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Select...</SelectItem>
                          <SelectItem value="Plaintiff">Plaintiff</SelectItem>
                          <SelectItem value="Defendant">Defendant</SelectItem>
                          <SelectItem value="Court">Court</SelectItem>
                          <SelectItem value="Third Party">Third Party</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div><Label className="text-xs">Document #</Label><Input className="h-9" data-testid="doc-number" value={filingForm.document_number} onChange={e => setFilingForm(f => ({...f, document_number: e.target.value}))} placeholder="Doc #" /></div>
                    <div className="md:col-span-2"><Label className="text-xs">Description</Label><Input className="h-9" data-testid="filing-description" value={filingForm.description} onChange={e => setFilingForm(f => ({...f, description: e.target.value}))} placeholder="Brief description of filing..." /></div>
                    <div><Label className="text-xs">Custom Deadline Date</Label><Input className="h-9" type="date" data-testid="custom-deadline" value={filingForm.deadline_date} onChange={e => setFilingForm(f => ({...f, deadline_date: e.target.value}))} /></div>
                  </div>
                  <Button size="sm" data-testid="add-filing-btn" onClick={addFiling} disabled={loading || !filingForm.filing_type || !filingForm.filing_date}>
                    {loading ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Plus className="w-3 h-3 mr-1" />}Add Filing
                  </Button>
                </CardContent>
              </Card>

              {/* Docket Timeline */}
              <Card>
                <CardHeader><CardTitle className="text-base flex items-center gap-2"><FileText className="w-4 h-4 text-blue-600" />Docket Timeline ({sortedFilings.length})</CardTitle></CardHeader>
                <CardContent>
                  {sortedFilings.length === 0 ? (
                    <p className="text-sm text-slate-500 text-center py-6">No filings yet. Add your first filing entry above.</p>
                  ) : (
                    <div className="space-y-2">
                      {sortedFilings.map((f, i) => (
                        <div key={f.id} className="flex gap-3 items-start" data-testid={`filing-entry-${i}`}>
                          <div className="flex flex-col items-center pt-1">
                            <div className={`w-2.5 h-2.5 rounded-full shrink-0 ${i === sortedFilings.length - 1 ? "bg-blue-500" : "bg-slate-300"}`} />
                            {i < sortedFilings.length - 1 && <div className="w-px flex-1 bg-slate-200 mt-1" />}
                          </div>
                          <div className="flex-1 pb-3 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-xs text-slate-400 font-mono">{f.filing_date}</span>
                              {f.document_number && <span className="text-xs text-slate-400">Doc #{f.document_number}</span>}
                              <CatBadge cat={f.category} />
                            </div>
                            <div className="text-sm font-semibold mt-0.5">{f.filing_type_name || f.filing_type}</div>
                            {f.filed_by && <div className="text-xs text-slate-500">Filed by: {f.filed_by}</div>}
                            {f.description && <div className="text-xs text-slate-600 mt-0.5">{f.description}</div>}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Deadlines */}
              {sortedDeadlines.length > 0 && (
                <Card>
                  <CardHeader><CardTitle className="text-base flex items-center gap-2"><Clock className="w-4 h-4 text-amber-600" />Deadlines ({sortedDeadlines.length})</CardTitle></CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {sortedDeadlines.map(dl => (
                        <div key={dl.id} className="flex items-center gap-3 p-2 rounded-lg bg-slate-50 border" data-testid={`deadline-${dl.id}`}>
                          <Calendar className="w-4 h-4 text-slate-400 shrink-0" />
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium">{dl.deadline_name}</div>
                            <div className="text-xs text-slate-500">{dl.deadline_date} | From: {dl.filing_type}</div>
                          </div>
                          <StatusBadge status={dl.status} date={dl.deadline_date} />
                          {dl.status === "pending" && (
                            <div className="flex gap-1 shrink-0">
                              <Button size="sm" variant="outline" className="h-7 text-green-700 border-green-200" onClick={() => markDeadline(dl.id, "met")} data-testid={`mark-met-${dl.id}`}><CheckCircle className="w-3 h-3" /></Button>
                              <Button size="sm" variant="outline" className="h-7 text-slate-500 border-slate-200" onClick={() => markDeadline(dl.id, "waived")} data-testid={`mark-waived-${dl.id}`}><XCircle className="w-3 h-3" /></Button>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          ) : (
            <Card><CardContent className="py-12 text-center text-slate-500">Select a docket to view details.</CardContent></Card>
          )}
        </TabsContent>

        {/* ═══ DEADLINES ═══ */}
        <TabsContent value="deadlines" className="mt-4 space-y-4">
          {upcomingDL.total_overdue > 0 && (
            <Card className="border-red-200 bg-red-50">
              <CardHeader><CardTitle className="text-base text-red-800 flex items-center gap-2"><AlertTriangle className="w-4 h-4" />Overdue ({upcomingDL.total_overdue})</CardTitle></CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {upcomingDL.overdue.map(dl => (
                    <div key={dl.id} className="flex items-center gap-3 p-2 bg-white rounded border border-red-200 cursor-pointer" onClick={() => openDocket(dl.docket_id)}>
                      <Flag className="w-4 h-4 text-red-600 shrink-0" />
                      <div className="flex-1">
                        <div className="text-sm font-medium text-red-800">{dl.deadline_name}</div>
                        <div className="text-xs text-red-600">{dl.case_title} — {dl.court_name}</div>
                      </div>
                      <span className="text-xs font-bold text-red-700">{dl.deadline_date}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
          <Card>
            <CardHeader><CardTitle className="text-base flex items-center gap-2"><Clock className="w-4 h-4 text-amber-600" />Upcoming ({upcomingDL.total_upcoming})</CardTitle></CardHeader>
            <CardContent>
              {upcomingDL.upcoming.length === 0 ? (
                <p className="text-sm text-slate-500 text-center py-6">No upcoming deadlines in the next 60 days.</p>
              ) : (
                <div className="space-y-2">
                  {upcomingDL.upcoming.map(dl => (
                    <div key={dl.id} className="flex items-center gap-3 p-2 rounded-lg bg-slate-50 border cursor-pointer" onClick={() => openDocket(dl.docket_id)}>
                      <Calendar className="w-4 h-4 text-amber-500 shrink-0" />
                      <div className="flex-1">
                        <div className="text-sm font-medium">{dl.deadline_name}</div>
                        <div className="text-xs text-slate-500">{dl.case_title} — {dl.court_name}</div>
                      </div>
                      <span className="text-xs font-bold text-amber-700">{dl.deadline_date}</span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ═══ AI PARSE ═══ */}
        <TabsContent value="ai-parse" className="mt-4 space-y-4">
          <Card>
            <CardHeader><CardTitle className="text-lg flex items-center gap-2"><Bot className="w-5 h-5 text-blue-600" />AI Docket Parser</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <p className="text-sm text-slate-500">Paste a docket sheet or case history text and AI will parse it into structured filing entries with auto-detected filing types and deadlines.</p>
              <textarea
                className="w-full min-h-[200px] px-3 py-2 rounded-md border text-sm font-mono"
                data-testid="docket-parse-input"
                value={parseText}
                onChange={e => setParseText(e.target.value)}
                placeholder={"Paste docket text here. Example:\n\n01/15/2024 - Doc 1 - COMPLAINT filed by John Smith (Plaintiff)\n01/22/2024 - Doc 5 - SUMMONS Returned Executed\n02/05/2024 - Doc 8 - ANSWER filed by Acme Corp (Defendant)\n03/01/2024 - Doc 12 - SCHEDULING ORDER - Discovery cutoff 09/01/2024\n04/15/2024 - Doc 18 - INTERROGATORIES served by Plaintiff\n05/15/2024 - Doc 22 - RESPONSES to Interrogatories\n06/01/2024 - Doc 25 - MOTION FOR SUMMARY JUDGMENT filed by Defendant\n06/15/2024 - Doc 28 - OPPOSITION to MSJ filed by Plaintiff\n07/01/2024 - Doc 30 - ORDER DENYING Motion for Summary Judgment"}
              />
              <Button data-testid="parse-docket-btn" onClick={parseDocket} disabled={parsing || !parseText.trim()} className="bg-blue-600 hover:bg-blue-700 text-white">
                {parsing ? <><Loader2 className="w-4 h-4 animate-spin mr-2" />Parsing...</> : <><Bot className="w-4 h-4 mr-2" />Parse with AI</>}
              </Button>
            </CardContent>
          </Card>

          {parseResult && (
            <Card className="border-green-200 bg-green-50/50">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base text-green-800 flex items-center gap-2"><CheckCircle className="w-4 h-4" />Parsed: {parseResult.case_title} ({parseResult.filing_count} filings)</CardTitle>
                  <Button size="sm" onClick={importParsed} disabled={loading} className="bg-green-600 hover:bg-green-700 text-white" data-testid="import-parsed-btn">
                    {loading ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Plus className="w-3 h-3 mr-1" />}Import All
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs mb-3">
                  {parseResult.judge && <div><strong>Judge:</strong> {parseResult.judge}</div>}
                  {parseResult.plaintiff && <div><strong>Plaintiff:</strong> {parseResult.plaintiff}</div>}
                  {parseResult.defendant && <div><strong>Defendant:</strong> {parseResult.defendant}</div>}
                  {parseResult.docket_number && <div><strong>Docket #:</strong> {parseResult.docket_number}</div>}
                </div>
                <div className="space-y-1">
                  {(parseResult.filings || []).map((f, i) => (
                    <div key={i} className="flex items-center gap-2 p-1.5 bg-white rounded border text-xs">
                      <span className="font-mono text-slate-400 w-20 shrink-0">{f.filing_date}</span>
                      <CatBadge cat={f.category} />
                      <span className="font-medium">{f.filing_type_name || f.filing_type}</span>
                      <span className="text-slate-500 truncate">{f.description}</span>
                      {f.generates_deadline && <span className="ml-auto text-amber-600 shrink-0">⏰ +{f.suggested_deadline_days}d</span>}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* ═══ ANALYTICS ═══ */}
        <TabsContent value="analytics" className="mt-4 space-y-4">
          {analytics?.summary ? (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {[
                  { label: "Active Dockets", value: analytics.summary.active_dockets, icon: Scale, color: "text-blue-600" },
                  { label: "Total Filings", value: analytics.summary.total_filings, icon: FileText, color: "text-purple-600" },
                  { label: "Avg Filings/Docket", value: analytics.summary.avg_filings_per_docket, icon: BarChart3, color: "text-teal-600" },
                  { label: "Deadline Compliance", value: `${analytics.summary.deadline_compliance_pct}%`, icon: CheckCircle, color: "text-green-600" },
                  { label: "Deadlines Met", value: analytics.summary.deadlines_met, icon: CheckCircle, color: "text-green-600" },
                  { label: "Deadlines Overdue", value: analytics.summary.deadlines_overdue, icon: AlertTriangle, color: "text-red-600" },
                  { label: "Motions Filed", value: analytics.summary.motions_filed, icon: Gavel, color: "text-orange-600" },
                  { label: "Motion Success Rate", value: `${analytics.summary.motion_success_rate_pct}%`, icon: Activity, color: "text-indigo-600" },
                ].map(s => (
                  <Card key={s.label}>
                    <CardContent className="py-3 flex items-center gap-3">
                      <s.icon className={`w-7 h-7 ${s.color}`} />
                      <div><div className="text-lg font-bold">{s.value}</div><div className="text-xs text-slate-500">{s.label}</div></div>
                    </CardContent>
                  </Card>
                ))}
              </div>

              {Object.keys(analytics.filings_by_category || {}).length > 0 && (
                <Card>
                  <CardHeader><CardTitle className="text-base">Filings by Category</CardTitle></CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {Object.entries(analytics.filings_by_category).sort(([,a],[,b]) => b - a).map(([cat, count]) => (
                        <div key={cat} className="flex items-center gap-2">
                          <CatBadge cat={cat} />
                          <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                            <div className="h-full bg-blue-500 rounded-full" style={{ width: `${Math.min((count / Math.max(analytics.summary.total_filings, 1)) * 100, 100)}%` }} />
                          </div>
                          <span className="text-sm font-bold w-8 text-right">{count}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {analytics.filings_by_type?.length > 0 && (
                <Card>
                  <CardHeader><CardTitle className="text-base">Top Filing Types</CardTitle></CardHeader>
                  <CardContent>
                    <div className="space-y-1.5">
                      {analytics.filings_by_type.map(([type, count]) => (
                        <div key={type} className="flex items-center justify-between text-sm">
                          <span className="text-slate-700">{type.replace(/_/g, " ")}</span>
                          <span className="font-bold text-blue-600">{count}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {analytics.filings_by_court_type && (
                <Card>
                  <CardHeader><CardTitle className="text-base">Court Type Distribution</CardTitle></CardHeader>
                  <CardContent>
                    <div className="flex gap-4">
                      {Object.entries(analytics.filings_by_court_type).map(([ct, count]) => (
                        <div key={ct} className="text-center">
                          <div className="text-2xl font-bold text-blue-600">{count}</div>
                          <div className="text-xs text-slate-500">{ct.charAt(0).toUpperCase() + ct.slice(1)} Filings</div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </>
          ) : (
            <Card><CardContent className="py-12 text-center text-slate-500">
              <BarChart3 className="w-10 h-10 mx-auto mb-3 text-slate-300" />
              No analytics data yet. Create dockets and add filings to see insights.
            </CardContent></Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
